import React, { useContext, useEffect, useMemo, useState } from "react";
import Helmet from "react-helmet";
import styled, { ThemeContext } from "styled-components";
import { fadeInCss } from "../styleBits";
import { useParams } from "react-router-dom";

import StoryItem from "../components/Story";
import MoreStories from "../components/MoreStories";
import StoriesEmptyState from "../components/StoryEmptyState";
import LoadError, { Loading } from "../components/LoadError";
import {
  StoryRecord,
  getLoadedStories,
  loadStories,
  visibleStories,
} from "../stories";

const StoryContainer = styled.div`
  ${fadeInCss}
  margin: 64px auto;
  max-width: 800px;

  a {
    text-decoration: none;
  }
`;

// Hidden stories stay reachable by direct link; they are only kept out of lists.
const findStory = (data: StoryRecord[], id: string | undefined) =>
  data.find((story) => story._id === id) ?? null;

const Story = (props) => {
  const { id } = useParams();
  // Seeded from the cache so arriving from the list paints the story at once,
  // and a "read more" link to another id swaps it within the same render.
  const [data, setData] = useState<StoryRecord[] | undefined>(getLoadedStories);
  const [failed, setFailed] = useState(false);
  const [retried, setRetried] = useState(false);
  const theme: any = useContext(ThemeContext);

  useEffect(() => {
    if (data || failed) return;
    let cancelled = false;
    loadStories().then(
      (loaded) => {
        if (!cancelled) setData(loaded);
      },
      () => {
        if (!cancelled) setFailed(true);
      }
    );
    return () => {
      cancelled = true;
    };
  }, [data, failed]);

  useEffect(() => {
    if (!retried || !data) return;
    document.getElementById("content")?.focus({ preventScroll: true });
    setRetried(false);
  }, [data, retried]);

  // undefined while loading, null when the id does not exist.
  const story = data ? findStory(data, id) : undefined;
  // Same order and filtering as the list page for "Read more stories".
  const stories = useMemo(() => (data ? visibleStories(data) : []), [data]);
  const description = useMemo(() => {
    if (!story) return null;
    return [story.author, story.title].join(";");
  }, [story]);

  if (failed) {
    return (
      <StoryContainer theme={theme}>
        <LoadError
          what="This story"
          onRetry={() => {
            setFailed(false);
            setRetried(true);
          }}
        />
      </StoryContainer>
    );
  }

  if (story === undefined) {
    return (
      <StoryContainer theme={theme}>
        <Loading what="story" />
      </StoryContainer>
    );
  }

  return (
    <StoryContainer theme={theme}>
      {story ? (
        <>
          <Helmet>
            <title>SignUp Media - {story.title}</title>
            <meta name="description" content={description} />
            <meta name="keywords" content={description} />
            <meta name="author" content={story.author} />
            <meta property="og:title" content={story.title} />
            {story.media?.type === "image" && (
              <meta property="og:image" content={story.media.value} />
            )}
            <meta
              property="og:url"
              content={`${window.location.origin}/story/${story._id}`}
            />
          </Helmet>
          <StoryItem preview={false} story={story} />
          <MoreStories stories={stories} current={story} />
        </>
      ) : (
        <StoriesEmptyState search={false} />
      )}
    </StoryContainer>
  );
};

export default Story;
